import React, { InputHTMLAttributes, FC, ReactElement, RefObject } from 'react';
import classNames from 'classnames';
import { Asset, CloseIcon, Loading, TooltipProps } from '@clearcover/atlas';
import { InfoTooltip } from 'Shared/components/InfoTooltip/InfoTooltip';
import { useUniqueId } from 'Shared/hooks/useUniqueId/useUniqueId';
import { isPresent } from 'Shared/utils/object';
import styles from './styles.module.css';

export type LabelProps = {
  htmlFor?: string;
  id?: string;
  label?: string;
  labelVariant?: 'floating' | 'standard' | 'floatingIcon';
  disabled?: boolean;
  className?: string;
  tooltip?: TooltipProps['tooltip']; // Add a tooltip next to the label for standard variant
  tooltipPosition?: TooltipProps['position'];
  tooltipLabel?: React.ReactNode;
  tooltipClassName?: string;
};

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  errorMessage?: string;
  sizeVariant?: 'small' | 'medium' | 'large' | 'extraLarge';
  floatingIcon?: boolean;
  tooltipPosition?: LabelProps['tooltipPosition'];
  inputRef?: RefObject<HTMLInputElement>;
  loading?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
};

type IconProps = {
  onIconClick?: () => void;
  icon?: ReactElement;
};

export type TextInputProps = IconProps & LabelProps & InputProps;

export const Input: FC<InputProps> = ({
  sizeVariant = 'medium',
  errorMessage,
  inputRef,
  className,
  floatingIcon,
  tooltipPosition,
  isClearable,
  onClear,
  ...inputProps
}) => {
  const inputCss = classNames(
    'fs-mask',
    styles.formControl,
    {
      [styles.large]: sizeVariant === 'large',
      [styles.medium]: sizeVariant === 'medium',
      [styles.small]: sizeVariant === 'small',
      [styles.hasError]: isPresent(errorMessage),
    },
    className
  );

  return <input ref={inputRef} className={inputCss} {...inputProps} />;
};

export const Label: FC<Omit<LabelProps, 'label'> & {
  children?: React.ReactNode;
}> = ({
  labelVariant = 'standard',
  disabled = false,
  htmlFor,
  id,
  children,
  className,
  tooltip,
  tooltipPosition,
  tooltipLabel,
  tooltipClassName,
}) => {
  const labelCss = classNames(
    {
      [styles.floatingLabel]: labelVariant === 'floating',
      [styles.standardLabel]: labelVariant === 'standard',
      [styles.floatingIconLabel]: labelVariant === 'floatingIcon',
      [styles.disabled]: disabled,
    },
    className
  );

  return (
    <>
      <label className={labelCss} htmlFor={htmlFor} id={id}>
        {children}
        {labelVariant === 'standard' && tooltip && (
          <div className={styles.labelTooltip}>
            <InfoTooltip
              tooltip={tooltip}
              position={tooltipPosition}
              size={16}
              tooltipLabel={tooltipLabel}
              className={tooltipClassName}
            />
          </div>
        )}
      </label>
    </>
  );
};

export const TextInput: FC<TextInputProps> = ({
  label,
  labelVariant = 'standard',
  className,
  icon,
  children,
  loading,
  tooltip,
  tooltipPosition,
  onIconClick,
  isClearable,
  onClear,
  ...inputProps
}) => {
  const { uniqueId } = useUniqueId();
  const id = `${inputProps.name}-${uniqueId}`;
  const isDisabled = inputProps.disabled || loading;

  const iconElement = icon ? (
    <div
      className={classNames(styles.icon, {
        [styles.iconClickable]: onIconClick,
      })}
      key={`icon-${inputProps.name}`}
      onClick={onIconClick}
    >
      {icon}
    </div>
  ) : null;

  const clearElement = isClearable ? (
    <div
      className={classNames(styles.clearIcon, {
        [styles.iconClickable]: onClear,
      })}
      key={`clear-${inputProps.name}`}
      onClick={onClear}
      data-testid={`clear-${inputProps.name}`}
    >
      <Asset description="Clear" width={14} height={14} icon={CloseIcon} />
    </div>
  ) : null;

  const inputElement = (
    <Input
      data-testid={inputProps.name}
      key={`input-${inputProps.name}`}
      id={id}
      autoComplete="new-password"
      {...inputProps}
      disabled={isDisabled}
    />
  );

  const hasValue = isPresent(inputProps.value) && inputProps.value !== '';

  const labelElement = (
    <Label
      key={`label-${inputProps.name}`}
      labelVariant={labelVariant}
      htmlFor={id}
      disabled={inputProps.disabled}
      tooltip={tooltip}
      tooltipPosition={tooltipPosition}
    >
      {label}
    </Label>
  );

  // Styling requires label to be after input for floating purposes
  // and vice-versa for standard label
  const formGroupElements = {
    floating: [iconElement, inputElement, labelElement],
    floatingIcon: [inputElement, labelElement, iconElement],
    standard: [labelElement, iconElement, inputElement],
  }[labelVariant];

  return (
    <div
      className={classNames(styles.formGroup, className, {
        [styles.hasIcon]: !!icon,
        [styles.hasFloatingIcon]: labelVariant === 'floatingIcon',
        [styles.hasValue]: hasValue,
        [styles.isClearable]: isClearable,
      })}
    >
      {formGroupElements}
      {children}
      {loading && <Loading className={styles.loadingIcon} />}
      {isClearable && !isDisabled && clearElement}
    </div>
  );
};
